/** @file Home of Popover component - the go-to component for creating floating UI elements. */
import { autoUpdate, computePosition, flip, hide, offset, shift, size, } from '@floating-ui/dom';
import prop from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { Maybe } from '@codegrade/maybe';
import * as utils from '@/utils';
import './style.css';
/**
 * Default ID generator used by LabelAsTooltip.
 *
 * @returns A unique ID string.
 */
const getDefaultPopoverId = () => `cg-popover-${utils.getUniqueId()}`;
/**
 * Take an event and return its currentTarget if it is a HTMLElement.
 *
 * @param evt Event.
 * @returns HTMLElement or undefined.
 */
function getEventCurrentTargetAsHTMLElement(evt) {
    if (evt?.currentTarget instanceof HTMLElement) {
        return evt.currentTarget;
    }
    return undefined;
}
/**
 * The default behavior when attaching a popover to its triggering element.
 *
 * > Middleware allow you to customize the behavior of the positioning
 * > and be as granular as you want, adding your own custom logic.
 * >
 * > `computePosition()` starts with initial positioning via placement — then middleware
 * > are executed as an in-between “middle” step of the initial placement computation
 * > and eventual return of data for rendering.
 * >
 * > Excerpt from <https://floating-ui.com/docs/middleware>.
 *
 * Default middleware selected:
 *
 * 1. Offset on main axis to create appropricate distance with triggering element.
 * 2. Shift - Shifts the floating element to keep it in view, with a decent amount of padding.
 * 3. Flip - Hide the floating element when the triggering element goes out of view.
 *
 * @param options Configuration options.
 * @returns Array of middleware.
 */
/** The default middleware. */
export const defaultPopoverFloatingUIMiddleware = [
    offset({ mainAxis: 4 }),
    flip({
        padding: 8,
        // Set to `floating` to flip when popover wll be out of bounds.
        elementContext: 'floating',
    }),
    shift({
        padding: 8,
        // Set to `floating` to shift when popover wll be out of bounds.
        elementContext: 'floating',
    }),
    size({
        padding: 8,
        /** Applies the size. */
        apply({ 
        /** The available height. */
        availableHeight, 
        /** The elements. */
        elements, }) {
            elements.floating.style.setProperty('--popover-available-height', `${availableHeight}px`);
            elements.floating.style.overflowY = 'auto';
        },
    }),
    hide({
        padding: 8,
        elementContext: 'reference',
    }),
];
/**
 * Popover provides the logic and basic DOM structure to create any floating and button-following UI.
 */
export const Popover = tsx.componentFactoryOf().create({
    name: 'popover',
    inheritAttrs: true,
    model: {
        prop: 'isOpen',
        event: 'toggle',
    },
    props: {
        /**
         * Assign one of the base CodeGrade base styling.
         *
         * Variant `base` - Minimal styling, best for making highly customized popovers.
         * Variant `tooltip` - The go-to style for tooltips.
         * Variant `dialog` - Useful as a floating content block with appropriate paddings and styles.
         */
        variant: prop.ofStringLiterals('basic', 'tooltip', 'dialog').default('basic'),
        /**
         * ID to assign the popover element.
         *
         * Optional, will generate a unique ID if not provided.
         */
        popoverId: prop(String).optional,
        /**
         * Use to programatically control open state in the parent.
         *
         * @example
         * <Popover
         *  isOpen={this.isChildPopoverOpen}
         *  onToggle={(newValue) => this.isChildPopoverOpen = newValue}
         * />
         */
        isOpen: prop(Boolean).default(false),
        /**
         * Should the popover renders a backdrop to dim elements behind.
         */
        backdrop: prop(Boolean).default(false),
        /** Trap keyboard tab focus within the popover when open, still allowing dismissal through clicks and esc. */
        trapFocus: prop(Boolean).default(false),
        /**
         * Configure the type of popover with HTML Popover API.
         *
         * "auto" - auto popovers can be "light dismissed" — this means that you can hide the
         * popover by clicking outside of it or pressing the Esc key. Showing an auto popover will
         * generally close other auto popovers that are already displayed, unless they are nested.
         *
         * "manual" - manual popovers cannot be "light dismissed" are not automatically closed.
         * Popovers must explicitly be displayed and closed using declarative show/hide/toggle buttons or JavaScript.
         * Multiple independent manual popovers can be shown simultaneously.
         *
         * More info: <https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/popover>.
         */
        popoverType: prop.ofStringLiterals('auto', 'manual').default('auto'),
        /**
         * Specify a placement value will attach the popover to the button that opens the popover.
         *
         * When it is undefined, the popover will not be attached to the triggering element.
         * It will act like a dialog.
         *
         * When it is a value with Placement type, it will follow the trigger element around.
         */
        placement: prop.ofType().optional,
        /**
         * An array of middleware for Floating UI to compute the positioning of the popover.
         *
         * More info: <https://floating-ui.com/docs/middleware>.
         */
        middleware: prop.ofArray().default(() => defaultPopoverFloatingUIMiddleware),
        /**
         * Customize the HTML tag used to render the wrapper element of this component.
         */
        containerTag: prop.ofType().default('div'),
        /**
         * Customize the HTML tag used to render the wrapper element of the popover itself.
         *
         * @example
         * Pass `dialog` to enjoy the behavior of a dialog element.
         * Docs on dialog: <https://developer.mozilla.org/en-US/docs/Web/HTML/Element/dialog>
         */
        popoverTag: prop.ofType().default('div'),
        /**
         * Attributes to apply on the popover element.
         *
         * This is different from adding attributes onto Popover component itself. Attributes added
         * onto Popover component itself will be applied to the wrapper element.
         */
        popoverAttrs: prop
            .ofType()
            .default(() => ({})),
        /**
         * Prop to decide when the popover content should be rendered and if the rendered nodes
         * should be kept.
         */
        lazyBehavior: prop.ofType().default('on-active-keep-rendered'),
    },
    /**
     * Internal State of the Popover component.
     *
     * @returns A state object.
     */
    data() {
        return {
            internalPopoverId: this.popoverId ?? getDefaultPopoverId(),
            hasBeenOpenedBefore: this.isOpen ?? false,
            internalIsOpen: this.isOpen,
            triggeringElement: Maybe.empty(),
            maybeFnCleanUpAutoUpdate: Maybe.empty(),
            placementData: Maybe.empty(),
        };
    },
    computed: {
        /**
         * The data that will be passed to scoped slots.
         *
         * @returns An object typed as PopoverScopedSlotData.
         */
        scopeData() {
            const isOpen = Boolean(this.internalIsOpen);
            return {
                popoverId: this.internalPopoverId,
                isOpen,
                show: evt => this.show(getEventCurrentTargetAsHTMLElement(evt)),
                hide: evt => this.hide(getEventCurrentTargetAsHTMLElement(evt)),
                toggle: evt => this.toggle(getEventCurrentTargetAsHTMLElement(evt)),
            };
        },
        /**
         * Styling for the popover element.
         *
         * @returns Styling that can be used in style prop of Vue components.
         */
        popoverStyle() {
            if (this.placement == null) {
                return undefined;
            }
            return this.placementData
                .map(({ x, y, hidden }) => {
                const invisible = !this.internalIsOpen || hidden;
                return {
                    transform: `translate(${utils.roundByDPR(x)}px,${utils.roundByDPR(y)}px)`,
                    display: invisible ? 'none' : undefined,
                };
            })
                .extract();
        },
        /** @returns A maybe of the popover, updates when internalPopoverId changes. */
        popoverElement() {
            if (this.$el == null || typeof this.$el.querySelector !== 'function') {
                // this.$el can be undefined despite what TypeScript says.
                // For example, it can be missing when we access this.popoverElement before `mounted`.
                // In these case, return an empty.
                return Maybe.empty();
            }
            return Maybe.fromNullable(this.$el.querySelector(`#${this.internalPopoverId}`)).chain(element => {
                if (element instanceof HTMLElement) {
                    return Maybe.of(element);
                }
                return Maybe.empty();
            });
        },
        /**
         * @returns A Maybe of the popover and the trigger element.
         *
         * Either element missing will make this Maybe a Nothing.
         */
        elementsForPositioning() {
            return Maybe.sequence([
                this.triggeringElement.altLazy(() => this.getPopoverTriggers().map(list => list[0])),
                this.popoverElement,
            ]).map(([element, popover]) => ({ element, popover }));
        },
        /**
         * @returns If the popover should be rendered to the DOM Tree.
         */
        shouldRenderPopoverContent() {
            switch (this.lazyBehavior) {
                case 'on-active-keep-rendered':
                    return this.internalIsOpen || this.hasBeenOpenedBefore;
                case 'on-active-discard-rendered':
                    return this.internalIsOpen;
                case 'pre-render': // FALL THROUGH
                default:
                    return true;
            }
        },
    },
    watch: {
        isOpen: {
            immediate: true,
            /** Watch Popover.isOpen and control internal state `internalIsOpen` with it. */
            handler() {
                if (this.isOpen != null) {
                    this.internalIsOpen = this.isOpen;
                }
            },
        },
        popoverId: {
            immediate: true,
            /** Update internal popover id when popoverId prop changes. */
            handler() {
                if (this.popoverId != null) {
                    this.internalPopoverId = this.popoverId;
                }
                else {
                    this.internalPopoverId = getDefaultPopoverId();
                }
            },
        },
        placement: {
            // This can be false because we have isInternalOpen watcher covering the checks here.
            immediate: false,
            /**
             * This watcher observe `placement` when it:
             * - Changes from undefined to some value -> compute attachment when popover is showing.
             * - Changes to undefined -> Cleans up attachment.
             *
             * Additionally, when placement changes, the position should be updated.
             */
            handler(newPlacement, oldPlacement) {
                if (newPlacement == null) {
                    // when placement changes to undefined, clean up autoUpdate.
                    this.cleanupAutoUpdate();
                }
                else if (oldPlacement == null && newPlacement != null && this.internalIsOpen) {
                    // when placement changes from undefined, and popover is open, re-init autoUpdate.
                    this.initAutoUpdate();
                }
                else if (this.internalIsOpen) {
                    // when popover is open and placement changes in anyway, update position.
                    this.updatePosition();
                }
            },
        },
    },
    methods: {
        /**
         * Get Elements that can show/hide/toggle the popover.
         *
         * @returns An array of such elements.
         */
        getPopoverTriggers() {
            const nodeList = this.$el.querySelectorAll(`[popovertarget=${this.internalPopoverId}]`);
            const result = [];
            nodeList.forEach(element => {
                if (element instanceof HTMLElement) {
                    result.push(element);
                }
            });
            return Maybe.fromPredicate(() => result.length > 0, result);
        },
        /**
         * Update the interal state `triggerElement` and returns a check.
         *
         * @param element The HTMLElement to save as the new triggering element.
         * @returns True if the element was already the last triggering element.
         */
        updateTriggerElement(element) {
            const mElement = Maybe.fromNullable(element);
            const wasTheSameElement = mElement
                .chain(el => this.triggeringElement.map(other => other === el))
                .orDefault(false);
            this.triggeringElement = mElement;
            return wasTheSameElement;
        },
        /** Function to control the popover's visibility. */
        toggle(target) {
            this.triggeringElement = Maybe.fromNullable(target);
            this.internalIsOpen = !this.internalIsOpen;
        },
        /** Function to control the popover's visibility. */
        show(target) {
            this.triggeringElement = Maybe.fromNullable(target);
            this.internalIsOpen = true;
        },
        /** Function to control the popover's visibility. */
        hide(target) {
            this.triggeringElement = Maybe.fromNullable(target);
            this.internalIsOpen = false;
        },
        /** Conditionally attach popover to the trigger element. */
        initAutoUpdate() {
            if (this.placement == null) {
                this.placementData = Maybe.empty();
                return;
            }
            this.cleanupAutoUpdate();
            this.maybeFnCleanUpAutoUpdate = this.elementsForPositioning.chain(({ element, popover }) => Maybe.of(autoUpdate(element, popover, this.updatePosition)));
        },
        /**
         * Update position of the popover.
         *
         * Calls computePosition from Floating UI and update component state with its result.
         */
        updatePosition() {
            this.elementsForPositioning.ifJust(async ({ element, popover }) => {
                const result = await computePosition(element, popover, {
                    strategy: 'fixed',
                    placement: this.placement,
                    middleware: this.middleware,
                });
                const { x, y, middlewareData } = result;
                const hidden = middlewareData.hide?.referenceHidden === true;
                this.placementData = Maybe.of({ hidden, x, y });
            });
        },
        /** Clean up Auto Update by Floating UI. */
        cleanupAutoUpdate() {
            this.maybeFnCleanUpAutoUpdate.ifJust(cleanup => {
                cleanup();
                this.maybeFnCleanUpAutoUpdate = Maybe.empty();
            });
        },
        /**
         * Handles keyboard navigation to contain focus within the popover.
         *
         * @param event The keyboard event.
         */
        handleFocusTrap(event) {
            if (event.key !== 'Tab' || !this.internalIsOpen) {
                return;
            }
            this.popoverElement.ifJust(popoverEl => {
                // Find all focusable elements within the modal.
                const focusableElements = Array.from(popoverEl.querySelectorAll('a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])')).filter(el => !el.hasAttribute('disabled') && !el.hasAttribute('aria-disabled'));
                if (focusableElements.length === 0) {
                    event.preventDefault();
                    return;
                }
                const firstElement = focusableElements[0];
                const lastElement = focusableElements[focusableElements.length - 1];
                if (event.shiftKey && document.activeElement === firstElement) {
                    event.preventDefault();
                    lastElement.focus();
                }
                else if (document.activeElement === lastElement) {
                    event.preventDefault();
                    firstElement.focus();
                }
            });
        },
    },
    /** Vue Lifecycle hook. */
    mounted() {
        /**
         * Watch `internalIsOpen` to do the following:
         * - Is now open - Compute attachment if placement is not null.
         * - Is now closed (hidden) - Clean up attachment.
         *
         * Also, calls togglePopover method on the popover HTML element.
         *
         * This is not in `watch` option as this should only start watching
         * after component is mounted.
         *
         * Otherwise the component will fail to mount since we are trying to
         * access this.$el in this watcher.
         *
         * The isOpen immediate watcher will trigger this internalIsOpen watcher,
         * which then tries to access this.$el before it is mounted.
         */
        this.$watch(() => this.internalIsOpen, (newValue, oldValue) => {
            if (this.internalIsOpen) {
                this.hasBeenOpenedBefore = true;
            }
            if (newValue && this.placement != null) {
                // Only when popover is shown and placement prop is specified,
                // we compute the attachment of the popover to the trigger element.
                this.initAutoUpdate();
            }
            else {
                // Otherwise, call the cleanUp function returned from autoUpdate.
                this.cleanupAutoUpdate();
            }
            // Tell the popover element to show or hide.
            this.popoverElement.ifJust(el => el.togglePopover(newValue));
            // Emit the toggle event when it is necessary to sync the state to the incoming prop.
            if (newValue !== oldValue) {
                tsx.emitOn(this, 'onToggle', newValue);
                tsx.emitOn(this, newValue ? 'onShow' : 'onHide');
            }
            // Focus Trap Logic
            if (this.trapFocus) {
                this.popoverElement.ifJust(el => {
                    if (newValue) {
                        // Popover is opening
                        el.addEventListener('keydown', this.handleFocusTrap);
                    }
                    else {
                        // Popover is closing
                        el.removeEventListener('keydown', this.handleFocusTrap);
                    }
                });
            }
        }, { immediate: true });
    },
    /** Vue Lifecycle hook. */
    beforeDestroy() {
        tsx.emitOn(this, 'onToggle', false);
        this.cleanupAutoUpdate();
    },
    /**
     * Render function of Popover.
     *
     * @returns VNode.
     */
    render() {
        const { containerTag: Container, popoverTag: PopoverContainer, popoverAttrs } = this;
        return (<Container 
        /**
         * Reset the cursor to be unset, so that we can render popovers on
         * elements that _do_ change the cursor, without having that cursor
         * _everywhere_ on the popover.
         */
        class="cursor-default">
                {utils.renderSlot(this, 'default', undefined)}
                {utils.renderScopedSlot(this.scopeData, this.$scopedSlots.button, utils.emptyVNode)}
                {utils.ifJustOrEmpty(utils.maybeRenderScopedSlot(this.scopeData, this.$scopedSlots.popover), popoverContent => (<PopoverContainer {...popoverAttrs} popover={this.popoverType} id={this.internalPopoverId} class={[
                    'cg-popover',
                    {
                        basic: 'cg-popover-basic',
                        tooltip: 'cg-popover-tooltip',
                        dialog: 'cg-popover-dialog',
                    }[this.variant], // Assign the correct base component styles.
                    {
                        // Prepare component to be positioned around.
                        'fixed inset-0 top-0 left-0 m-0': this.placement != null,
                        'with-backdrop': this.backdrop === true,
                    },
                    popoverAttrs.class,
                ]} style={[this.popoverStyle, popoverAttrs.style]} onToggle={(evt) => {
                    const element = getEventCurrentTargetAsHTMLElement(evt);
                    if (element != null) {
                        // Update the internal state of this component to match the
                        // current state of rendered popover.
                        // Needed to capture automatic dismissal of the popover.
                        this.internalIsOpen = element.matches(':popover-open');
                    }
                }}>
                            {utils.ifOrEmpty(this.shouldRenderPopoverContent, () => popoverContent)}
                        </PopoverContainer>))}
            </Container>);
    },
});
